import isEqual from 'lodash.isequal'

import { log } from './debug'
import computedValues, { getDisplayValues } from './computedValues'
import {
  dispatchAction,
  getOpenInfoBoxFn,
  getCloseInfoBoxFn,
  getPageNavHandlers,
  getResetUserDataFn,
  getSetUserDataFn,
  getUserDataFn,
  triggerFormValidation,
  trackCustomEvent,
} from './controllerHelpers/utils'
import getUrlDict from './getUrlDict'
import { Flow, UserData, ComputedField as ComputedFieldType } from '../api/models/types'
import { Controller } from '@hotwired/stimulus'
import contextFunctionsHelpers from './contextFunctionsHelpers'
import { getComponentElement, isBuilderEditingMode } from './utils'

type TriggerContext = {
  isChanged: boolean
  inputs: string[]
  oldInputValues: string[]
  newInputValues: string[]
  lastUserDataCopy?: {}
  lastUserDataValues?: string[]
}
export default class ComputedField {
  flowController: Controller
  fieldData: ComputedFieldType
  flow: Flow
  lastResult: null
  lastUserDataValues: {} | null
  input_triggers: string
  key: string
  userData: { [key: string]: string }

  constructor(computedField: ComputedFieldType, flow: Flow, flowController: Controller) {
    this.flowController = flowController
    this.fieldData = computedField || {}
    this.flow = flow
    this.lastResult = null
    this.lastUserDataValues = null
  }

  data() {
    return this.fieldData
  }

  async compute(userData: { [x: string]: string }, before: UserData) {
    //* Do not run computed fields marked to be skipped in the builder when in editing mode
    if (isBuilderEditingMode() && this.fieldData.do_not_run_in_builder) return

    /* Retrieve Memoized Inputs and compare inputs to new set of user data */
    const inputsChanged = this.haveInputsChanged(userData)
    log('ComputedField', 'compute, inputsChanged', this.fieldData.key, inputsChanged)
    if (!inputsChanged.isChanged) return
    const result = await this.computeResult(userData, inputsChanged)
    log('ComputedField', 'compute, result', this.fieldData.key, result)
    /* Skip setting in user data if the results are equal */
    if (this.lastResult !== null && isEqual(result, this.lastResult)) return
    this.lastResult = result
    log('ComputedField', 'compute, { result, key: this.fieldData.key }', this.fieldData.key, {
      result,
      key: this.fieldData.key,
    })

    if (window.Savvy.record_computed_logs) {
      if (!window.Savvy) window.Savvy = {}
      if (!window.Savvy.computed_logs) window.Savvy.computed_logs = []

      window.Savvy.computed_logs.push({
        flow_id: this.flow.id,
        timestamp: Date.now(),
        computed_field_id: this.fieldData.id,
        computed_field_code: this.fieldData.code || '',
        user_data_before_snapshot: before as UserData,
        user_data_after_snapshot: userData as UserData,
        input_keys: this.fieldData.key,
        input_values: this.fieldData.inputs || [],
        inputs_causing_recompute: inputsChanged.inputs,
        result: result,
      })
    }

    return { result, key: this.fieldData.key }
  }

  haveInputsChanged(userData: Record<string, unknown>) {
    const f = {}

    const selfKey = this.fieldData.key
    if (this.fieldData && this.fieldData.use_full_user_data) {
      Object.entries(userData).forEach(([key, value]) => {
        if (selfKey !== key) f[key] = value
      })
    } else {
      if (!this.fieldData.input_triggers || this.fieldData.input_triggers.length === 0) {
        Object.entries(this.fieldData.fields || {}).forEach(([key, text]) => {
          f[key] = computedValues(String(text), userData)
        })
        const inputs = this.fieldData.inputs || []
        inputs.forEach((key) => (f[key] = userData[key]))
      } else {
        const inputTriggers = this.fieldData.input_triggers || []
        inputTriggers.forEach((key) => (f[key] = userData[key]))
      }
    }

    /* Replace with a proper equality check */
    const inputsMatch = isEqual(f, this.lastUserDataValues)

    const inputs: string[] =
      !inputsMatch && this.lastUserDataValues
        ? Object.keys(f).filter((key) => f[key] !== this.lastUserDataValues?.[key])
        : []

    const lastUserDataCopy = { ...this.lastUserDataValues }
    const oldInputValues = inputs.map((input) => lastUserDataCopy?.[input])
    const newInputValues = inputs.map((input) => f?.[input])
    /* Cleanup */
    this.lastUserDataValues = f
    return {
      isChanged: !inputsMatch,
      inputs,
      oldInputValues,
      newInputValues,
      oldUserData: lastUserDataCopy,
    }
  }

  async computeResult(userData: { [x: string]: any }, triggerContext: TriggerContext) {
    const field = this.fieldData
    const fieldValues = this.getFieldValues(userData)

    switch (field.formula) {
      case 'random_group':
        return userData
          ? [undefined, null].includes(userData[field.key])
            ? getRandomGroup(fieldValues.num_groups)
            : userData[field.key]
          : undefined
      case 'url_query': {
        const dict = getUrlDict() || {}
        return dict[fieldValues.query_name] || ''
      }
      case 'custom': {
        const res = await this.getCustomResult(userData, triggerContext)
        return typeof res === 'number' && (isNaN(res) || res === Infinity) ? '_error' : res
      }

      default: {
        const predefinedFormula = getFormulas().find((formula) => formula.key === field.formula)

        return predefinedFormula && predefinedFormula.formula
          ? predefinedFormula.formula(fieldValues)
          : null
      }
    }
  }

  getCustomResult(userData: { [x: string]: string }, triggerContextResult: TriggerContext) {
    if (!this.fieldData.code) return null

    const fields = { _display_values: '' }
    if (this.fieldData.use_full_user_data || this.fieldData.include_full_user_data) {
      const userDataCopy = { ...userData }
      delete userDataCopy[this.key]
      Object.entries(userDataCopy).forEach(([k, v]) => (fields[k] = v))
    } else if (this.fieldData.inputs)
      this.fieldData.inputs.forEach((key) => (fields[key] = userData[key]))

    const setUserData = getSetUserDataFn(this.flowController)
    const getUserData = getUserDataFn(this.flowController)
    const openInfoBox = async (...args) => (await getOpenInfoBoxFn(this.flowController))(...args)
    const closeInfoBox = async () => (await getCloseInfoBoxFn(this.flowController))()
    const resetUserData = getResetUserDataFn(this.flowController)
    const triggerAction = (action: string) => dispatchAction(this.flowController, action)
    const triggerValidation = () => triggerFormValidation(this.flowController)
    const contextFunctions = {
      getUserData,
      resetUserData,
      setUserData,
      getCurrentEmbeddableId: () => this.flow.id,
      getComponentElement: (key) => getComponentElement(this.flowController, key),
      openInfoBox: (value: string) => openInfoBox({ detail: { value } }),
      closeInfoBox: () => closeInfoBox(),
      triggerAction,
      triggerValidation,
      trackCustomEvent: (customEventName, customEventProps) =>
        trackCustomEvent(this.flow.id, customEventName, customEventProps),
      helpers: contextFunctionsHelpers,
      ...getPageNavHandlers(this.flowController),
    }
    fields._display_values = String(getDisplayValues(this.userData, this.flow))
    try {
      const triggerContext = {
        inputs_causing_recompute: triggerContextResult.inputs,
        old_input_values: triggerContextResult.oldInputValues,
        new_input_values: triggerContextResult.newInputValues,
        old_user_data: triggerContextResult.lastUserDataCopy,
        new_user_data: fields,
      }
      const code = `${this.fieldData.code}; result(${JSON.stringify(
        fields
      )}, contextFunctions, ${JSON.stringify(triggerContext)})`

      return eval(code)
    } catch (e) {
      console.warn(`Error in ComputedField ${this.fieldData.key} (Flow ID: ${this.flow.id})`, e)
      return '_error'
    }
  }

  getFieldValues(userData: Record<string, unknown>) {
    const f = {
      num_groups: 0,
      query_name: '',
      email: '',
      num1: 0,
      num2: 0,
    }

    const selfKey = this.fieldData.key
    if (
      this.fieldData &&
      (this.fieldData.use_full_user_data || this.fieldData.include_full_user_data)
    ) {
      Object.entries(userData).forEach(([key, value]) => {
        if (selfKey !== key) f[key] = value
      })
    } else {
      Object.entries(this.fieldData.fields || {}).forEach(([key, text]) => {
        f[key] = computedValues(String(text), userData)
      })
      const inputs = this.fieldData.inputs || []
      inputs.forEach((key) => (f[key] = userData[key]))
    }
    return f
  }
}

function getFormulas() {
  const formulas = [
    {
      key: 'random_group',
      name: 'Random Group',
      group: 'split_testing',
      fields: [
        {
          key: 'num_groups',
          type: 'number',
        },
      ],
    },
    {
      key: 'url_query',
      name: 'Url Query Value',
      group: 'Web Page',
      fields: [
        {
          key: 'query_name',
          type: 'string',
        },
      ],
    },
    {
      key: 'email_validation',
      name: 'Email Validation',
      group: 'validation',
      fields: [
        {
          key: 'email',
          type: 'string',
        },
      ],
      formula({ email }) {
        const re =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        return re.test(String(email).toLowerCase())
      },
    },
    {
      key: 'add',
      name: 'Add',
      group: 'math',
      fields: [
        {
          key: 'num1',
          type: 'number',
        },
        {
          key: 'num2',
          type: 'number',
        },
      ],
      formula({ num1, num2 }) {
        return getNumber(num1) + getNumber(num2)
      },
    },
    {
      key: 'subtract',
      name: 'Subtract',
      group: 'math',
      fields: [
        {
          key: 'num1',
          type: 'number',
        },
        {
          key: 'num2',
          type: 'number',
        },
      ],
      formula({ num1, num2 }) {
        return getNumber(num1) - getNumber(num2)
      },
    },
    {
      key: 'multiple',
      name: 'Multiple',
      group: 'math',
      fields: [
        {
          key: 'num1',
          type: 'number',
        },
        {
          key: 'num2',
          type: 'number',
        },
      ],
      formula({ num1, num2 }) {
        return getNumber(num1) * getNumber(num2)
      },
    },
    {
      key: 'divide',
      name: 'Divide',
      group: 'math',
      fields: [
        {
          key: 'num1',
          type: 'number',
        },
        {
          key: 'num2',
          type: 'number',
        },
      ],
      formula({ num1, num2 }) {
        return getNumber(num1) / getNumber(num2)
      },
    },
    {
      key: 'custom',
      name: 'Custom',
      group: 'custom',
    },
  ]
  return formulas
}

function getRandomGroup(numGroups: number) {
  if (!numGroups) return null

  const randomGroup = Math.random() * numGroups

  return parseInt(String(randomGroup))
}

function getNumber(val) {
  return typeof val === 'string' ? parseInt(val) : val
}
